<script>
import { mapGetters, mapActions } from 'vuex'

export default {
	name: 'StatData',
	props: {
		userTag: {
			type: String,
		},
		isGo: {
			type: Boolean,
			default: false,
		},
	},
	computed: {
		...mapGetters('dashboard', [
			'getDashboardData',
			'isLoading',
			'getError',
			'getSelectedFishingYear',
		]),
		isUser() {
			return this.$user
		},
		getTradeLink() {
			if (this.userTag === 'subject') return `/${this.$i18n.locale}/account/trade/`
			return ``
		},
		getLicenseLink() {
			if (this.userTag === 'subject') return `/${this.$i18n.locale}/account/info/?tab=license`
			return ``
		},
		statData() {
			return this.getDashboardData
		},
		getSelectedFishingYearLabel() {
			return this.getSelectedFishingYear?.start?.split('-')?.reverse()?.join('.') || ''
		},
	},
	mounted() {
		// Data will be loaded by the dashboard store when WellCome component initializes
	},
	methods: {
		...mapActions('dashboard', ['fetchDashboardData']),
		async refreshData() {
			await this.fetchDashboardData(this.userTag)
		},
	},
}
</script>

<template>
	<div
		class="bg-white/90 p-[30px]  shadow-main rounded-[10px] backdrop-blur-[10px] grid grid-cols-1 gap-[10px] lg:grid-cols-3"
		:class="{
			'pt-0 rounded-t-[0px]': isGo
		}"
	>
		<template v-if="statData">
			<div
				:style="{
					background: `radial-gradient(71.01% 71.01% at 82.36% 28.99%, #ACD2F9 0%, #DDEEFF 100%)`,
				}"
				class="flex flex-col items-start relative rounded-[16px] py-[23px] px-[20px] grow min-h-[207px] hover:no-underline"
			>
				<svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:class="'block w-[40px] h-[40px] mb-[11px]'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><circle cx="20" cy="20" r="20" fill="#52A5FC"/><path d="M24.19 10h-8.38C12.17 10 10 12.17 10 15.81v8.37c0 3.65 2.17 5.82 5.81 5.82h8.37c3.64 0 5.81-2.17 5.81-5.81v-8.38C30 12.17 27.83 10 24.19 10zm-6.28 14.19c0 .64-.52 1.16-1.17 1.16-.64 0-1.16-.52-1.16-1.16v-3.26c0-.64.52-1.16 1.16-1.16.65 0 1.17.52 1.17 1.16v3.26zm6.51 0c0 .64-.52 1.16-1.16 1.16-.65 0-1.17-.52-1.17-1.16v-8.38c0-.64.52-1.16 1.17-1.16.64 0 1.16.52 1.16 1.16v8.38z" fill="#fff"/></svg>
				<div
					class="font-main font-semibold text-[24px] text-third leading-[1.33] mb-[11px] no-underline"
				>
					{{ Number(statData.sum_total_fish_quota) | formatNumber }} кг
				</div>
				<div
					class="grow font-main font-medium text-[16px] text-fourth leading-[1.5] mb-[11px] no-underline"
				>
					{{ $t('dashboard.my_quota.application.quota.text') }}
				</div>
				<div class="mt-2 text-[12px] italic no-underline">
					{{ $t('headers.data_start_20250701', { year: getSelectedFishingYearLabel }) }}
				</div>
			</div>
			<router-link
				:to="getLicenseLink"
				:style="{
					background: `radial-gradient(71.01% 71.01% at 82.36% 28.99%, #FFF4DE 0%, #FEE8BD 100%)`,
				}"
				:class="{
					'pointer-events-none': !getLicenseLink,
				}"
				class="flex flex-col items-start relative rounded-[16px] py-[23px] px-[20px] grow hover:no-underline"
			>
				<svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:class="'block w-[40px] h-[40px] mb-[11px]'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><circle cx="20" cy="20" r="20" fill="#52A5FC"/><path d="M24.19 10h-8.38C12.17 10 10 12.17 10 15.81v8.37c0 3.65 2.17 5.82 5.81 5.82h8.37c3.64 0 5.81-2.17 5.81-5.81v-8.38C30 12.17 27.83 10 24.19 10zm-6.28 14.19c0 .64-.52 1.16-1.17 1.16-.64 0-1.16-.52-1.16-1.16v-3.26c0-.64.52-1.16 1.16-1.16.65 0 1.17.52 1.17 1.16v3.26zm6.51 0c0 .64-.52 1.16-1.16 1.16-.65 0-1.17-.52-1.17-1.16v-8.38c0-.64.52-1.16 1.17-1.16.64 0 1.16.52 1.16 1.16v8.38z" fill="#fff"/></svg>
				<div class="font-main font-semibold text-[24px] text-third leading-[1.33] mb-[11px]">
					<span>{{ Number(statData.sum_total_fish_permission) | formatNumber }} кг </span>
					<span>/</span>
					<span> {{ Number(statData.percentage_quota_permission) | formatNumber }} %</span>
				</div>
				<div class="grow font-main font-medium text-[16px] text-fourth leading-[1.5] mb-[11px]">
					{{ $t('dashboard.my_quota.application.permission.text') }}
				</div>
				<div class="mt-2 text-[12px] italic">
					{{ $t('headers.data_start_elicense_20250701', { year: getSelectedFishingYearLabel }) }}
				</div>
			</router-link>
			<router-link
				:to="getTradeLink"
				:style="{
					background: `radial-gradient(71.01% 71.01% at 82.36% 28.99%, #DCFCE7 0%, #EDFFF3 100%)`,
				}"
				:class="{
					'pointer-events-none': !getTradeLink,
				}"
				class="flex flex-col items-start relative rounded-[16px] py-[23px] px-[20px] grow hover:no-underline"
			>
				<svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:class="'block w-[40px] h-[40px] mb-[11px]'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><circle cx="20" cy="20" r="20" fill="#52A5FC"/><path d="M24.19 10h-8.38C12.17 10 10 12.17 10 15.81v8.37c0 3.65 2.17 5.82 5.81 5.82h8.37c3.64 0 5.81-2.17 5.81-5.81v-8.38C30 12.17 27.83 10 24.19 10zm-6.28 14.19c0 .64-.52 1.16-1.17 1.16-.64 0-1.16-.52-1.16-1.16v-3.26c0-.64.52-1.16 1.16-1.16.65 0 1.17.52 1.17 1.16v3.26zm6.51 0c0 .64-.52 1.16-1.16 1.16-.65 0-1.17-.52-1.17-1.16v-8.38c0-.64.52-1.16 1.17-1.16.64 0 1.16.52 1.16 1.16v8.38z" fill="#fff"/></svg>
				<div class="font-main font-semibold text-[24px] text-third leading-[1.33] mb-[11px]">
					<span>{{ Number(statData.sum_total_fish_trade) | formatNumber }} кг </span>
					<span>/</span>
					<span> {{ Number(statData.percentage_trade_permission) | formatNumber }} %</span>
				</div>
				<div class="grow font-main font-medium text-[16px] text-fourth leading-[1.5] mb-[11px]">
					{{ $t('dashboard.my_quota.application.trade.text') }}
				</div>
				<div class="mt-2 text-[12px] italic">
					{{ $t('headers.data_start_trade_20250701', { year: getSelectedFishingYearLabel }) }}
				</div>
			</router-link>
		</template>

		<!-- Show empty state when no data -->
		<template v-else>
			<div class="col-span-full flex flex-col items-center justify-center py-12 text-center">
				<svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:class="'block w-16 h-16 mb-4 opacity-50'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><circle cx="20" cy="20" r="20" fill="#52A5FC"/><path d="M24.19 10h-8.38C12.17 10 10 12.17 10 15.81v8.37c0 3.65 2.17 5.82 5.81 5.82h8.37c3.64 0 5.81-2.17 5.81-5.81v-8.38C30 12.17 27.83 10 24.19 10zm-6.28 14.19c0 .64-.52 1.16-1.17 1.16-.64 0-1.16-.52-1.16-1.16v-3.26c0-.64.52-1.16 1.16-1.16.65 0 1.17.52 1.17 1.16v3.26zm6.51 0c0 .64-.52 1.16-1.16 1.16-.65 0-1.17-.52-1.17-1.16v-8.38c0-.64.52-1.16 1.17-1.16.64 0 1.16.52 1.16 1.16v8.38z" fill="#fff"/></svg>
				<div class="font-main font-medium text-[16px] text-fourth leading-[1.5] mb-2">
					{{ $t('messages.no_dashboard_data') }}
				</div>
				<div class="font-main font-normal text-[14px] text-second leading-[1.5]">
					{{
						$t('messages.select_fishing_period', { year: getSelectedFishingYearLabel }) ||
						'Выберите период рыболовства для отображения данных'
					}}
				</div>
			</div>
		</template>
	</div>
</template>
